/**
 * Material PDF Viewer Modal Component
 *
 * Displays PDF files in a modal with View (read-only) or Print mode.
 * Features:
 * - View mode: Disables printing, right-click, and keyboard shortcuts
 * - Print mode: Allows printing via browser print dialog
 * - Annotation mode: Drawing overlay for teaching. Notes are NOT saved automatically —
 *   they live only while the modal is open unless the teacher clicks "Save notes"
 *   (which keeps them for RETENTION_DAYS days). See docs/教材笔记-改为默认不保存与7天保留-2026-08-25.md
 * - Fullscreen mode: For iPad projection/screen sharing
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';
import { getMaterialNotes, saveMaterialNotes, deleteMaterialNotes, RETENTION_DAYS } from '../../api/materialAnnotationApi';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: ${props => props.$fullscreen ? '0' : '12px'};
  width: ${props => props.$fullscreen ? '100%' : '90%'};
  max-width: ${props => props.$fullscreen ? '100%' : '1000px'};
  /* 关键：非全屏也用"确定高度"，否则 height:auto + 内容区 min-height:0 会让内容区
     塌缩成 0 高度（桌面/平板等不触发移动端规则的宽度都会出现），只能靠全屏才能看。
     vh 兜底、dvh 优先（规避移动端地址栏）。 */
  height: ${props => props.$fullscreen ? '100vh' : '90vh'};
  height: ${props => props.$fullscreen ? '100dvh' : '90dvh'};
  max-height: ${props => props.$fullscreen ? '100vh' : '90vh'};
  max-height: ${props => props.$fullscreen ? '100dvh' : '90dvh'};
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.$fullscreen ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'};

  /* 手机/平板（含 iPad，断点提到 1024px）：铺满屏幕并钉死视口高度，底部缩放栏始终可见 */
  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    border-radius: 0;
    /* 100vh 作为兜底，再用动态视口高度 100dvh 规避地址栏导致的溢出 */
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 18px;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  @media (max-width: 1024px) {
    padding: 10px 12px;
    h3 { font-size: 15px; }
  }
`;

const TitleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModeBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.mode === 'print' ? '#10b981' : '#3b82f6'};
  color: white;
`;

const IconButton = styled.button`
  background: ${props => props.$active ? '#3b82f6' : '#f3f4f6'};
  border: 1px solid ${props => props.$active ? '#3b82f6' : '#d1d5db'};
  border-radius: 6px;
  cursor: pointer;
  padding: 8px 12px;
  font-size: 14px;
  color: ${props => props.$active ? 'white' : '#4b5563'};
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${props => props.$active ? '#2563eb' : '#e5e7eb'};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0;

  &:hover {
    color: #1f2937;
  }
`;

/* ===== 「笔记不会自动保存」告知弹窗 =====
   校方要求：老师在**开始标注之前**就被告知笔记不会留下，而不是关闭时才弹窗。
   看过一次可以勾「Don't show this again」，之后不再打扰（同样的说明在 Teaching Tips 里长期保留）。 */
const NoticeOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  /* 盖在教材弹窗（z-index 1000）之上 */
  z-index: 1100;
`;

const NoticeCard = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;

  h4 {
    margin: 0;
    font-size: 17px;
    color: #1f2937;
  }
`;

const NoticeBody = styled.div`
  padding: 20px;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;

  p {
    margin: 0 0 12px;
  }

  strong {
    color: #b45309;
  }
`;

const NoticeFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-wrap: wrap;
`;

const NoticeCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
`;

const NoticeButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const NoticePrimaryButton = styled.button`
  background: #3b82f6;
  border: 1px solid #3b82f6;
  color: white;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #2563eb;
  }
`;

const NoticeSecondaryButton = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  color: #4b5563;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
  }
`;

const AnnotationToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 24px;
  background: #fef3c7;
  border-bottom: 1px solid #fde68a;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolLabel = styled.span`
  font-size: 12px;
  color: #92400e;
  font-weight: 500;
`;

const ColorButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected ? '#1f2937' : 'transparent'};
  background: ${props => props.$color};
  cursor: pointer;
  outline: ${props => props.$selected ? '2px solid white' : 'none'};
  outline-offset: -2px;

  &:hover {
    transform: scale(1.1);
  }
`;

const SizeSlider = styled.input`
  width: 80px;
  cursor: pointer;
`;

const ToolButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.$active ? '#f59e0b' : '#d1d5db'};
  border-radius: 4px;
  background: ${props => props.$active ? '#fef3c7' : 'white'};
  color: ${props => props.$active ? '#92400e' : '#4b5563'};
  font-size: 12px;
  cursor: pointer;
  font-weight: ${props => props.$active ? '600' : '400'};

  &:hover {
    background: ${props => props.$active ? '#fde68a' : '#f3f4f6'};
  }
`;

const ClearButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #ef4444;
  border-radius: 4px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #fee2e2;
  }
`;

const TextInputOverlay = styled.textarea`
  background: rgba(255, 255, 255, 0.85);
  border: 2px dashed ${props => props.$color || '#FF0000'};
  border-radius: 4px;
  padding: 4px 6px;
  font-size: ${props => props.$fontSize || 16}px;
  font-family: Arial, sans-serif;
  color: ${props => props.$color || '#FF0000'};
  outline: none;
  resize: none;
  min-width: 120px;
  min-height: ${props => (props.$fontSize || 16) + 12}px;
  line-height: 1.3;
  overflow: hidden;
  display: block;
  /* 父层在标注模式下 user-select:none，这里要显式放开，否则 iOS 里文字框选不了字、粘贴菜单不出 */
  user-select: text;
  -webkit-user-select: text;
  -webkit-touch-callout: default;
`;

const TextConfirmBar = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 4px;
`;

const TextConfirmButton = styled.button`
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  background: ${props => props.$primary ? '#10b981' : '#6b7280'};
  color: white;

  &:hover {
    opacity: 0.9;
  }
`;

const ModalContent = styled.div`
  flex: 1 1 0;
  /* 关键：flex 子项默认 min-height:auto 不会收缩，会被超长内容（连续滚动的所有页）
     撑大导致底部缩放栏被挤出屏幕；min-height:0 让它在弹窗内正常滚动、底栏始终可见 */
  min-height: 0;
  overflow: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f3f4f6;
  position: relative;
  /* 平滑的移动端惯性滚动 */
  -webkit-overflow-scrolling: touch;

  /* View mode restrictions */
  ${props => props.$viewOnly && !props.$annotating && `
    user-select: none;
    -webkit-user-select: none;
  `}

  /* 标注模式：禁掉 iPadOS 的长按选择。老师写字时手掌压在教材上，Safari 会把页面 canvas
     当成图片选中（蓝色高亮 + 拷贝/查询/翻译菜单），要点一下教材外面才能取消（2026-09-05 真机复现）。
     touch-action:none 挡不住这个手势，只能用 user-select + -webkit-touch-callout 关掉。 */
  ${props => props.$annotating && `
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  `}

  @media (max-width: 1024px) {
    padding: 8px;
  }

  @media print {
    ${props => props.$viewOnly && `
      display: none !important;
    `}
  }
`;

/* 教材页面本体（react-pdf 渲染出的 canvas）在任何模式下都不允许被长按选中 / 弹出拷贝菜单：
   教材是受控内容，不希望用户随手把整页当图片选走。 */
const pageCanvasNoSelect = `
  & canvas {
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }
`;

const PdfContainer = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
  ${pageCanvasNoSelect}
`;

/* 连续滚动模式：所有页竖向堆叠，家长直接上下滑动查看 */
const PagesScroll = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const PageCard = styled.div`
  background: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  max-width: 100%;
  position: relative;
  ${pageCanvasNoSelect}
`;

const PageBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  pointer-events: none;
  z-index: 2;
`;

const AnnotationCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: ${props => props.$active ? 'auto' : 'none'};
  cursor: ${props => {
    if (props.$textMode) return 'text';
    if (props.$eraser) return 'cell';
    return 'crosshair';
  }};
  /* 标注时一律 touch-action:none —— iPadOS Safari 把 Apple Pencil 也纳入触摸手势体系，
     旧的 pan-y 会让浏览器抢走竖向位移，表现为「竖着写字页面就跟着滚」（Vic Park 教师反馈）。
     关掉浏览器手势后，教材滚动改由 useAnnotationPointer 手动实现（拖动 + 惯性）。 */
  touch-action: ${props => props.$active ? 'none' : 'auto'};
  /* 画布本身永远不该被长按选中 */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
`;

/* 标注模式下的临时提示（手指用途、检测到手写笔等） */
const HintToast = styled.div`
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.92);
  color: #ffffff;
  font-size: 13px;
  line-height: 1.4;
  padding: 8px 14px;
  border-radius: 999px;
  z-index: 20;
  pointer-events: none;
  max-width: 92%;
  text-align: center;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    padding: 8px 12px;
    gap: 8px;
    /* 给iOS底部安全区留出空间 */
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
  }
`;

const PageControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    gap: 8px;
  }
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* 移动端加大点按区域 */
  @media (max-width: 1024px) {
    padding: 10px 14px;
    min-height: 44px;
  }
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: #4b5563;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$primary ? '#10b981' : '#6b7280'};
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 24px;
  text-align: center;
  color: #ef4444;
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 24px;
  padding-left: 24px;
  border-left: 1px solid #e5e7eb;

  @media (max-width: 1024px) {
    margin-left: 0;
    padding-left: 0;
    border-left: none;
  }
`;

const ZoomButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* 移动端加大缩放按钮的点按区域 */
  @media (max-width: 1024px) {
    width: 44px;
    height: 44px;
    font-size: 20px;
  }
`;

const ZoomInfo = styled.span`
  font-size: 12px;
  color: #6b7280;
  min-width: 45px;
  text-align: center;
`;

/* 「不再提醒」记在 localStorage：跟着这台设备的这个浏览器走，换设备会再提醒一次，
   对「老师换了台 iPad 上课」来说反而是对的 —— 提醒成本低，笔记丢了成本高。 */
const NOTICE_DISMISSED_KEY = 'uplus.annotationNotice.dismissed';

const isNoticeDismissed = () => {
  try {
    return window.localStorage.getItem(NOTICE_DISMISSED_KEY) === '1';
  } catch (e) {
    // 无痕模式 / 禁用存储时读不到，那就每次都提醒
    return false;
  }
};

const rememberNoticeDismissed = () => {
  try {
    window.localStorage.setItem(NOTICE_DISMISSED_KEY, '1');
  } catch (e) {
    // 存不上就算了，下次再提醒一次不影响使用
  }
};

// Color options for annotation
const COLORS = [
  '#FF0000', // Red
  '#FF6600', // Orange
  '#FFCC00', // Yellow
  '#00CC00', // Green
  '#0066FF', // Blue
  '#9900FF', // Purple
  '#000000', // Black
];

// ===== 指针路由（Apple Pencil / 手指 / 鼠标）=====
// 笔抬起后这段时间内新出现的触点视为手掌余波，直接忽略。
// 别设太长：老师写完常常紧接着就要滑动教材，窗口太大会变成「划不动」。
// 真正的掌托（写字时手掌一直压着）靠 penActive 全程屏蔽，与这个窗口无关。
const PALM_LOCKOUT_MS = 300;
// 「笔正贴在屏幕上」最多认这么久：pointerup / pointercancel 在真机上会丢
// （切 App、系统手势打断、Safari 偶发丢事件）。没有这个上限的话，一次丢失的抬笔事件
// 会让 penActive 永远为 true，之后手指既画不了也滚不动——正是「完全写不了字」的经典成因。
const PEN_ACTIVE_MAX_MS = 1500;
// 触点表里的僵尸触点同理：超过这个时间没更新就当它已经离开屏幕，
// 否则会被误判成「双指」，单指书写变成滚动。
const STALE_TOUCH_MS = 5000;
// 按接触面判手掌：必须同时满足「绝对够大」和「比本设备见过的最小触点大 PALM_CONTACT_RATIO 倍」。
// 只用固定阈值不行 —— iPadOS Safari 给普通手指上报的 width/height 就是 63×63（2026-09-05 真机实测），
// 旧的「≥60 即手掌」把所有手指都当成了手掌：笔一出现后，手指既画不了也滚不动（单指/双指都不行）。
// 真正的掌托主要靠 penActive + PALM_LOCKOUT_MS 挡住，这条只是兜底，宁可漏判也不能把手指误杀。
const PALM_CONTACT_PX = 60;
const PALM_CONTACT_RATIO = 2;

// 画布像素坐标 → 归一化坐标（0..1）。笔的压感一并记下来，回放时才有粗细变化。
const normPoint = (canvas, pos, ev) => {
  const W = canvas && canvas.width ? canvas.width : 1;
  const H = canvas && canvas.height ? canvas.height : 1;
  const x = Math.round((pos.x / W) * 10000) / 10000;
  const y = Math.round((pos.y / H) * 10000) / 10000;
  if (ev && ev.pointerType === 'pen' && ev.pressure > 0) {
    return [x, y, Math.round(ev.pressure * 100) / 100];
  }
  return [x, y];
};

// 屏幕坐标 → 画布像素坐标（画布内部像素尺寸可能与 CSS 显示尺寸不同，需按比例换算）
const canvasPos = (canvas, clientX, clientY) => {
  if (!canvas) return { x: 0, y: 0 };
  const rect = canvas.getBoundingClientRect();
  const sx = rect.width ? canvas.width / rect.width : 1;
  const sy = rect.height ? canvas.height / rect.height : 1;
  return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
};

/**
 * ===== 矢量笔画模型 =====
 *
 * 标注不再存成整张画布的 PNG（`toDataURL`），而是存每一笔的轨迹：
 *
 *   { t: 'pen'|'eraser', c: '#RRGGBB', w: 0.004, p: [[x, y, pressure?], ...] }
 *   { t: 'text', c: '#RRGGBB', s: 0.02, x, y, v: '文字' }
 *
 * 坐标与线宽全部**按页面宽高归一化到 0..1**。这样换来三件事：
 * 1. **Undo 成为可能** —— 位图没有「一笔」的概念，只能整页擦掉；矢量弹一个数组元素即可。
 * 2. **能上云** —— 一份教材的笔记通常只有几 KB（PNG 快照是每页几百 KB）。
 * 3. **缩放/换设备不糊不错位** —— 旧实现是把旧 PNG 拉伸重绘。
 */
const strokeLineWidth = (stroke, point, canvasWidth) => {
  const base = stroke.w * canvasWidth;
  const pressure = point && point.length > 2 ? point[2] : null;
  if (stroke.t === 'pen' && pressure != null && pressure > 0) {
    return Math.max(0.5, base * (0.4 + 1.3 * pressure));
  }
  return Math.max(0.5, base);
};

/** 把一笔画到画布上（坐标从归一化还原成画布像素）。 */
const drawStroke = (ctx, stroke, W, H) => {
  if (!stroke) return;

  if (stroke.t === 'text') {
    ctx.globalCompositeOperation = 'source-over';
    ctx.font = `${Math.round(stroke.s * W)}px Arial, sans-serif`;
    ctx.fillStyle = stroke.c;
    ctx.textBaseline = 'top';
    const lineHeight = stroke.s * W * 1.3;
    String(stroke.v).split('\n').forEach((line, i) => {
      ctx.fillText(line, stroke.x * W, stroke.y * H + i * lineHeight);
    });
    return;
  }

  const pts = stroke.p || [];
  if (!pts.length) return;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalCompositeOperation = stroke.t === 'eraser' ? 'destination-out' : 'source-over';
  ctx.strokeStyle = stroke.t === 'eraser' ? '#000000' : stroke.c;

  if (pts.length === 1) {
    // 单点（轻轻一戳）：画一个圆点，否则什么都看不到
    const r = strokeLineWidth(stroke, pts[0], W) / 2;
    ctx.fillStyle = stroke.t === 'eraser' ? '#000000' : stroke.c;
    ctx.beginPath();
    ctx.arc(pts[0][0] * W, pts[0][1] * H, r, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  for (let i = 1; i < pts.length; i += 1) {
    ctx.lineWidth = strokeLineWidth(stroke, pts[i], W);
    ctx.beginPath();
    ctx.moveTo(pts[i - 1][0] * W, pts[i - 1][1] * H);
    ctx.lineTo(pts[i][0] * W, pts[i][1] * H);
    ctx.stroke();
  }
};

/** 整页重绘：清空后按顺序回放所有笔画。Undo / 缩放 / 载入云端笔记都走这里。 */
const redrawCanvas = (canvas, strokes) => {
  if (!canvas || !canvas.width || !canvas.height) return;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  (strokes || []).forEach(stroke => drawStroke(ctx, stroke, canvas.width, canvas.height));
  ctx.globalCompositeOperation = 'source-over';
};

/**
 * 标注画布的指针路由，翻页模式与连续滚动模式共用。
 *
 * 2026-08-23 修复 Vic Park 教师反馈的三个真机问题：
 * 1. **写字时页面会滑动**：画布原来是 `touch-action: pan-y`，而 iPadOS Safari 把 Apple Pencil
 *    也当触摸手势，竖向笔画被浏览器当成滚动，`pointerdown` 里的 preventDefault 已经晚了。
 *    现在标注时画布 `touch-action: none`，浏览器不再插手；教材滚动改为这里手动实现
 *    （改 scrollTop/scrollLeft + 松手惯性），于是「笔书写」与「手指滑动」可以同时成立。
 * 2. **手指写不了字**：手指书写默认关闭且没有任何提示。父组件改为「笔优先」的自动策略
 *    （没检测到手写笔时手指直接能写），这里在手指被判为滚动时回调一次提示。
 * 3. **掌托误画**：笔正在书写、或刚抬起 700ms 内的触点一律忽略；接触面过大的触点也忽略。
 *
 * 另外，手指书写开启时**双指拖动**仍可滚动教材（此前 touch-action:none 会把双指也吃掉，
 * 老师一开手指书写就滑不动教材）；被双指打断的半截笔画会回滚到上一次持久化的画面。
 */
const useAnnotationPointer = ({
  canvasRef,
  scrollContainerRef,
  isAnnotating,
  isEraser,
  isTextMode,
  brushColor,
  brushSize,
  fingerDrawing,
  penStateRef,
  onPenSeen,
  onFingerScroll,
  onTextTap,
  onStrokeCommit, // 一笔画完：把矢量笔画交给父组件（进历史栈、标记待保存）
  redrawPage,     // 整页重绘（回滚半截笔画时用）
  onDebug, // 仅 /dev/annotation-lab 传入：把每次指针判定结果吐出去，方便真机排查
}) => {
  const drawingRef = useRef(false);
  const drawIdRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const moveCountRef = useRef(0);
  const currentStrokeRef = useRef(null); // 正在画的这一笔（归一化坐标）
  const touchesRef = useRef(new Map()); // pointerId -> { x, y }，用于判断单指/双指
  const panRef = useRef(null);          // 手指拖动滚动的状态
  const inertiaRef = useRef(null);

  useEffect(() => () => {
    if (inertiaRef.current) cancelAnimationFrame(inertiaRef.current);
  }, []);

  const stopInertia = () => {
    if (inertiaRef.current) cancelAnimationFrame(inertiaRef.current);
    inertiaRef.current = null;
  };

  // 清掉「按下后再没消息」的僵尸触点（丢失的 pointerup 会留下它们）
  const pruneStaleTouches = () => {
    const now = performance.now();
    touchesRef.current.forEach((pt, id) => {
      if (now - pt.t > STALE_TOUCH_MS) touchesRef.current.delete(id);
    });
  };

  // 多指时取触点中心，单指就是该触点
  const touchCenter = () => {
    const pts = Array.from(touchesRef.current.values());
    if (!pts.length) return null;
    const sum = pts.reduce((acc, pt) => ({ x: acc.x + pt.x, y: acc.y + pt.y }), { x: 0, y: 0 });
    return { x: sum.x / pts.length, y: sum.y / pts.length };
  };

  const startPan = () => {
    stopInertia();
    const c = touchCenter();
    if (!c) return;
    panRef.current = { x: c.x, y: c.y, vx: 0, vy: 0, t: performance.now() };
  };

  const movePan = () => {
    const el = scrollContainerRef && scrollContainerRef.current;
    const p = panRef.current;
    const c = touchCenter();
    if (!el || !p || !c) return;
    const dx = c.x - p.x;
    const dy = c.y - p.y;
    el.scrollTop -= dy;
    el.scrollLeft -= dx;
    const now = performance.now();
    const dt = now - p.t;
    if (dt > 0) {
      p.vx = (dx / dt) * 16; // 换算成「每帧位移」
      p.vy = (dy / dt) * 16;
    }
    p.x = c.x;
    p.y = c.y;
    p.t = now;
  };

  const endPan = () => {
    const el = scrollContainerRef && scrollContainerRef.current;
    const p = panRef.current;
    panRef.current = null;
    if (!el || !p) return;
    if (performance.now() - p.t > 80) return; // 停住后再松手：不甩惯性
    let vx = p.vx;
    let vy = p.vy;
    if (Math.abs(vx) < 1 && Math.abs(vy) < 1) return;
    const step = () => {
      el.scrollTop -= vy;
      el.scrollLeft -= vx;
      vx *= 0.94;
      vy *= 0.94;
      if (Math.abs(vx) > 0.4 || Math.abs(vy) > 0.4) {
        inertiaRef.current = requestAnimationFrame(step);
      } else {
        inertiaRef.current = null;
      }
    };
    inertiaRef.current = requestAnimationFrame(step);
  };

  // 半截笔画被双指滚动打断：丢掉这一笔并整页重绘，不留下多余的一道
  const abortStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    drawIdRef.current = null;
    currentStrokeRef.current = null;
    if (redrawPage) redrawPage();
  };

  const touchSize = (e) => Math.max(e.width || 0, e.height || 0);

  // 返回判定为手掌的原因（'pen-active' / 'lockout' / 'size'），不是手掌返回 null。
  // 原因会进调试页的判定日志，真机上一眼能看出是哪条规则在误杀手指。
  const palmReason = (e) => {
    const st = penStateRef.current;
    // 从没出现过手写笔的设备（老师只用手指）完全不做掌托抑制：
    // 没有笔要保护，却可能把粗一点的手指触点误判成手掌，变成「怎么都画不出来」。
    if (!st.announced) return null;
    const since = performance.now() - st.lastPenAt;
    if (st.penActive && since < PEN_ACTIVE_MAX_MS) return 'pen-active';
    if (since < PALM_LOCKOUT_MS) return 'lockout';
    const size = touchSize(e);
    if (size >= PALM_CONTACT_PX && st.minTouchPx > 0 && size >= st.minTouchPx * PALM_CONTACT_RATIO) return 'size';
    return null;
  };

  // 记录本设备见过的最小触点尺寸，作为「正常手指」的基准（见 PALM_CONTACT_RATIO 的注释）。
  // 只在触点没被时间规则拒掉时记录，避免把写字时压着的手掌当成基准。
  const noteTouchSize = (e) => {
    const size = touchSize(e);
    if (size <= 0) return;
    const st = penStateRef.current;
    if (!st.minTouchPx || size < st.minTouchPx) st.minTouchPx = size;
  };

  // 线宽：手写笔按压感动态变化，其余用固定 brushSize
  const lineWidthFor = (ev) => {
    if (ev.pointerType === 'pen' && ev.pressure > 0) {
      return Math.max(0.5, brushSize * (0.4 + 1.3 * ev.pressure));
    }
    return brushSize;
  };

  const debug = (act, e, extra) => {
    if (!onDebug) return;
    onDebug(Object.assign({
      act,
      type: e.pointerType,
      w: Math.round(e.width || 0),
      h: Math.round(e.height || 0),
      touches: touchesRef.current.size,
      fingerDrawing,
    }, extra));
  };

  const onPointerDown = (e) => {
    if (e.pointerType === 'pen') {
      penStateRef.current.penActive = true;
      penStateRef.current.lastPenAt = performance.now();
      if (onPenSeen) onPenSeen();
    }
    if (!isAnnotating) { debug('not-annotating', e); return; }

    if (e.pointerType === 'touch') {
      const palm = palmReason(e);
      if (palm) { e.preventDefault(); debug(`palm-ignored:${palm}`, e); return; } // 手掌：既不书写也不滚动
      noteTouchSize(e);
      pruneStaleTouches();
      touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() });
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* 忽略捕获失败 */ }
      const multiTouch = touchesRef.current.size >= 2;
      // 双指 = 滚动（不论手指书写开关）；单指且未开手指书写 = 滚动
      if (multiTouch || !fingerDrawing) {
        e.preventDefault();
        abortStroke();
        startPan();
        debug(multiTouch ? 'pan-2finger' : 'pan-1finger', e);
        if (!multiTouch && onFingerScroll) onFingerScroll();
        return;
      }
    } else {
      // 笔 / 鼠标落下：中断可能还在滑行的惯性滚动
      panRef.current = null;
      stopInertia();
    }

    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* 忽略捕获失败 */ }

    if (isTextMode) {
      debug('text', e);
      if (onTextTap) onTextTap(canvasPos(canvasRef.current, e.clientX, e.clientY));
      return;
    }

    debug('draw-start', e);
    moveCountRef.current = 0;
    drawIdRef.current = e.pointerId;
    const canvas = canvasRef.current;
    const startPos = canvasPos(canvas, e.clientX, e.clientY);
    lastPosRef.current = startPos;
    // 线宽按画布宽度归一化，换缩放/换设备回放时粗细才一致
    currentStrokeRef.current = {
      t: isEraser ? 'eraser' : 'pen',
      c: brushColor,
      w: (isEraser ? brushSize * 3 : brushSize) / (canvas ? canvas.width : 1),
      p: [normPoint(canvas, startPos, e)],
    };
    drawingRef.current = true;
  };

  const onPointerMove = (e) => {
    if (e.pointerType === 'touch' && touchesRef.current.has(e.pointerId)) {
      touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, t: performance.now() });
      if (panRef.current) {
        e.preventDefault();
        movePan();
        return;
      }
    }
    if (e.pointerType === 'pen') penStateRef.current.lastPenAt = performance.now();
    if (!drawingRef.current || drawIdRef.current !== e.pointerId || isTextMode) return;
    e.preventDefault();
    moveCountRef.current += 1;
    const canvas = canvasRef.current;
    const ctx = canvas && canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.strokeStyle = isEraser ? '#000000' : brushColor;
    // 合并中间点（getCoalescedEvents）让高速笔画更顺滑不断线
    const native = e.nativeEvent;
    const points = (native.getCoalescedEvents && native.getCoalescedEvents().length)
      ? native.getCoalescedEvents()
      : [native];
    for (const ev of points) {
      const pt = canvasPos(canvas, ev.clientX, ev.clientY);
      ctx.lineWidth = isEraser ? brushSize * 3 : lineWidthFor(ev);
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      lastPosRef.current = pt;
      // 立刻画是为了跟手，同时把归一化后的点记进这一笔，供 undo / 保存 / 重绘回放
      if (currentStrokeRef.current) currentStrokeRef.current.p.push(normPoint(canvas, pt, ev));
    }
  };

  const onPointerUp = (e) => {
    if (e.pointerType === 'pen') {
      penStateRef.current.penActive = false;
      penStateRef.current.lastPenAt = performance.now();
    }
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* 忽略 */ }

    if (e.pointerType === 'touch') {
      touchesRef.current.delete(e.pointerId);
      if (panRef.current) {
        if (touchesRef.current.size === 0) endPan();
        else startPan(); // 还剩手指：重设拖动基准，避免中心跳变
      }
    }

    if (drawingRef.current && drawIdRef.current === e.pointerId) {
      drawingRef.current = false;
      drawIdRef.current = null;
      debug('draw-end', e, { moves: moveCountRef.current, strokePoints: currentStrokeRef.current ? currentStrokeRef.current.p.length : 0 });
      const stroke = currentStrokeRef.current;
      currentStrokeRef.current = null;
      if (stroke && stroke.p.length && onStrokeCommit) onStrokeCommit(stroke);
    }
  };

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp };
};

/**
 * 连续滚动模式下的「可标注单页」：一个 PDF 页 + 覆盖其上的标注画布。
 * 每一页都拥有自己的画布，因此老师在滚动浏览时可以直接在当前看到的页面上标注，
 * 不需要切回翻页模式、也不会跳回第一页（更接近 iPad 的书写体验）。
 * 画笔颜色/粗细/文字工具等由父组件通过 props 下发；笔迹存到共享的 strokesRef（按页码，矢量）。
 */
const ScrollAnnotatablePage = ({
  pageNumber,
  numPages,
  renderWidth,
  isAnnotating,
  isEraser,
  isTextMode,
  brushColor,
  brushSize,
  fontSize,
  fingerDrawing,
  strokesRef,
  redrawToken,
  onStrokeCommit,
  onNaturalSize,
  scrollContainerRef,
  penStateRef,
  onPenSeen,
  onFingerScroll,
  onDebug,
}) => {
  const canvasRef = useRef(null);
  const aspectRef = useRef(null); // 本页原始高/宽比
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [textPos, setTextPos] = useState(null);
  const [textVal, setTextVal] = useState('');

  // 页面加载后记录原始宽高比，并据此计算画布尺寸
  const handlePageLoad = (page) => {
    const nW = page.originalWidth || page.width;
    const nH = page.originalHeight || page.height;
    if (nW && nH) {
      aspectRef.current = nH / nW;
      if (onNaturalSize) onNaturalSize({ width: nW, height: nH });
      setDims({ width: renderWidth, height: Math.round(renderWidth * (nH / nW)) });
    }
  };

  // 渲染宽度变化（缩放/窗口自适应）时，按已知比例重算画布尺寸
  useEffect(() => {
    if (aspectRef.current) {
      setDims({ width: renderWidth, height: Math.round(renderWidth * aspectRef.current) });
    }
  }, [renderWidth]);

  // 画布尺寸变化、或父组件要求重绘（undo / 清空 / 载入云端笔记）时：回放本页所有笔画。
  // 矢量回放意味着放大后依然清晰，不像旧实现那样把 PNG 拉伸糊掉。
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dims.width === 0 || dims.height === 0) return;
    canvas.width = dims.width;
    canvas.height = dims.height;
    redrawCanvas(canvas, strokesRef.current[pageNumber]);
  }, [dims, pageNumber, strokesRef, redrawToken]);

  const redrawPage = () => redrawCanvas(canvasRef.current, strokesRef.current[pageNumber]);

  const handleStrokeCommit = (stroke) => onStrokeCommit(pageNumber, stroke);

  const commitText = (pos, val) => {
    const canvas = canvasRef.current;
    if (!canvas || !val.trim()) return;
    // 文字同样按页面宽高归一化后入库，回放时才不会随缩放跑位
    onStrokeCommit(pageNumber, {
      t: 'text',
      c: brushColor,
      s: fontSize / canvas.width,
      x: pos.x / canvas.width,
      y: pos.y / canvas.height,
      v: val,
    });
    // 笔画是边画边显示的，文字不是：提交后立刻重绘本页把它画出来
    redrawCanvas(canvas, strokesRef.current[pageNumber]);
  };

  // 文字工具：点击画布放置输入框（已有未确认的文字先落到画布）
  const handleTextTap = (pos) => {
    if (textPos && textVal.trim()) commitText(textPos, textVal);
    setTextPos(pos);
    setTextVal('');
  };

  const pointerHandlers = useAnnotationPointer({
    canvasRef,
    scrollContainerRef,
    isAnnotating,
    isEraser,
    isTextMode,
    brushColor,
    brushSize,
    fingerDrawing,
    penStateRef,
    onPenSeen,
    onFingerScroll,
    onTextTap: handleTextTap,
    onStrokeCommit: handleStrokeCommit,
    redrawPage,
    onDebug,
  });

  const okText = () => {
    if (textPos && textVal.trim()) commitText(textPos, textVal);
    setTextPos(null);
    setTextVal('');
  };
  const cancelText = () => {
    setTextPos(null);
    setTextVal('');
  };

  return (
    <PageCard>
      {numPages > 1 && <PageBadge>{pageNumber} / {numPages}</PageBadge>}
      <Page
        pageNumber={pageNumber}
        width={renderWidth}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onLoadSuccess={handlePageLoad}
        loading={null}
      />
      <AnnotationCanvas
        ref={canvasRef}
        $active={isAnnotating}
        $eraser={isEraser}
        $textMode={isTextMode}
        {...pointerHandlers}
      />
      {isAnnotating && textPos && (
        <div style={{ position: 'absolute', left: `${textPos.x}px`, top: `${textPos.y}px`, zIndex: 10 }}>
          <TextInputOverlay
            value={textVal}
            onChange={(e) => {
              setTextVal(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); cancelText(); }
              else if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); okText(); }
              e.stopPropagation();
            }}
            $color={brushColor}
            $fontSize={fontSize}
            autoFocus
            rows={1}
          />
          <TextConfirmBar>
            <TextConfirmButton $primary onClick={okText}>OK</TextConfirmButton>
            <TextConfirmButton onClick={cancelText}>Cancel</TextConfirmButton>
          </TextConfirmBar>
        </div>
      )}
    </PageCard>
  );
};

const MaterialPdfViewerModal = ({
  show,
  onClose,
  pdfUrl,
  materialName,
  mode = 'view', // 'view' or 'print'
  onPrintComplete,
  materialId,     // 有它才开启「云端笔记」（保存 / 载入）；不传则只在本次会话内有效
  onPointerDebug, // 仅 /dev/annotation-lab 传入，用于真机排查指针判定
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 缩放倍率：相对于"适配宽度"的倍数（1 = 正好铺满可用宽度）
  const [zoomMultiplier, setZoomMultiplier] = useState(1.0);

  // 触摸设备（手机/iPad）默认用连续滚动模式，桌面默认翻页模式
  const isTouchDevice = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
  const [continuousScroll, setContinuousScroll] = useState(isTouchDevice);

  // 可用渲染宽度（由容器宽度测量得到）与PDF页面的原始尺寸
  const [containerWidth, setContainerWidth] = useState(0);
  const [naturalPageSize, setNaturalPageSize] = useState({ width: 0, height: 0 });

  // Annotation states
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [brushColor, setBrushColor] = useState('#FF0000');
  const [brushSize, setBrushSize] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  // 手指书写策略：null = 自动 —— 检测到 Apple Pencil 就让手指负责滚动、笔负责书写；
  // 一直没检测到笔（老师只用手指）则手指直接可以写。true/false = 老师在工具栏里手动指定。
  // 修复「没有手写笔的老师点了 Annotate 完全写不了字，只能截图记笔记」。
  const [fingerDrawPref, setFingerDrawPref] = useState(null);
  const [penDetected, setPenDetected] = useState(false);
  const fingerDrawing = fingerDrawPref === null ? !penDetected : fingerDrawPref;
  // 笔的实时状态，用于掌托抑制：penActive = 笔正贴在屏幕上，lastPenAt = 笔最后一次出现的时刻
  const penStateRef = useRef({ penActive: false, lastPenAt: 0, announced: false, minTouchPx: 0 });
  // 标注模式下的临时提示
  const [hint, setHint] = useState(null);
  const hintTimerRef = useRef(null);

  // Text tool states
  const [isTextMode, setIsTextMode] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  const [fontSize, setFontSize] = useState(16);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Page dimensions
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });

  // 重绘令牌：undo / redo / 清空 / 载入云端笔记后 +1，各页画布据此回放笔迹
  const [redrawToken, setRedrawToken] = useState(0);
  // 撤销/重做是否可用（真正的数据在 historyRef 里，这里只是驱动按钮的禁用态）
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  // 云端笔记状态：idle / loading / saving / saved / error
  const [notesStatus, setNotesStatus] = useState('idle');
  const [notesSavedAt, setNotesSavedAt] = useState(null);
  const [notesDirty, setNotesDirty] = useState(false);
  // 「笔记不会自动保存」告知弹窗：进入标注模式前先弹一次，勾了不再提醒就不弹
  const [showNotSavedNotice, setShowNotSavedNotice] = useState(false);
  const [noticeDontShowAgain, setNoticeDontShowAgain] = useState(false);

  // Refs
  const canvasRef = useRef(null);
  const modalRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const modalContentRef = useRef(null);
  const swipeRef = useRef(null); // 记录单指滑动起点，用于左右滑动翻页

  // 笔迹（矢量）：{ [页码]: 笔画数组 }。这是标注的唯一真源，画布只是它的一次渲染。
  const strokesRef = useRef({});
  // 撤销/重做历史。每一项记录「哪些页在这次操作前是什么样」，因此一次 Clear All 也能整体撤销。
  const historyRef = useRef({ past: [], future: [] });
  // 「本次打开是否已从云端载入过」标记（没有自动保存，所以不再需要防抖计时器）
  const loadedForRef = useRef(null);
  // 每次改动 +1。保存是异步的，用它判断「请求发出后有没有又画了新东西」，
  // 避免把保存期间新增的笔画错误地标记成已保存。
  const changeSeqRef = useRef(0);

  // ===== 自适应宽度 / 缩放计算 =====
  const FALLBACK_WIDTH = 900;
  const availableWidth = containerWidth > 0 ? containerWidth : FALLBACK_WIDTH;
  // "适配宽度"：页面铺满可用宽度但不超过原始尺寸（桌面不放大避免模糊）；再乘以用户缩放倍率
  const fitWidth = naturalPageSize.width > 0
    ? Math.min(naturalPageSize.width, availableWidth)
    : availableWidth;
  const renderWidth = Math.max(120, Math.round(fitWidth * zoomMultiplier));

  // 测量可用容器宽度（随窗口/全屏/模式变化自适应）
  useEffect(() => {
    if (!show) return undefined;
    const el = modalContentRef.current;
    if (!el) return undefined;
    const measure = () => {
      const style = window.getComputedStyle(el);
      const padL = parseFloat(style.paddingLeft) || 0;
      const padR = parseFloat(style.paddingRight) || 0;
      setContainerWidth(Math.max(0, el.clientWidth - padL - padR));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [show, isFullscreen, continuousScroll]);

  // 根据渲染宽度同步标注画布尺寸（仅翻页模式用到画布）
  useEffect(() => {
    if (naturalPageSize.width > 0) {
      const aspect = naturalPageSize.height / naturalPageSize.width;
      setPageDimensions({ width: renderWidth, height: Math.round(renderWidth * aspect) });
    }
  }, [renderWidth, naturalPageSize.width, naturalPageSize.height]);

  // 打开新文档时重置状态
  useEffect(() => {
    if (!show) return;
    setPageNumber(1);
    setZoomMultiplier(1.0);
    setNaturalPageSize({ width: 0, height: 0 });
    setLoading(true);
    setError(null);
    strokesRef.current = {};
    historyRef.current = { past: [], future: [] };
    setCanUndo(false);
    setCanRedo(false);
    setNotesDirty(false);
    setNotesSavedAt(null);
    setNotesStatus('idle');
    setRedrawToken(t => t + 1);
  }, [pdfUrl, show]);

  // Prevent right-click and print shortcuts in view mode
  useEffect(() => {
    if (!show || mode !== 'view') return;

    const preventContext = (e) => {
      e.preventDefault();
    };

    const preventPrint = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        alert('Printing is not allowed in View mode');
      }
    };

    document.addEventListener('contextmenu', preventContext);
    document.addEventListener('keydown', preventPrint);

    return () => {
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('keydown', preventPrint);
    };
  }, [show, mode]);

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // ===== 标注模式下的临时提示 =====
  const showHint = useCallback((msg) => {
    setHint(msg);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => setHint(null), 3500);
  }, []);

  useEffect(() => () => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, []);

  // 手指被判为「滚动」时提示一次，免得老师以为写不了字是功能坏了
  // ===== 笔迹的读写与历史 =====

  /** 记一次「操作前的快照」到历史栈。参数是受影响页码的数组。 */
  const pushHistory = useCallback((pages) => {
    const entry = pages.map(page => ({ page, prev: strokesRef.current[page] || [] }));
    historyRef.current.past.push(entry);
    // 一旦有新操作，重做链失效（和所有编辑器的行为一致）
    historyRef.current.future = [];
    // 历史无限增长没意义，留最近 100 步足够老师纠错
    if (historyRef.current.past.length > 100) historyRef.current.past.shift();
    setCanUndo(true);
    setCanRedo(false);
    changeSeqRef.current += 1;
    setNotesDirty(true);
  }, []);

  /** 一笔画完（或落下一段文字）：进历史栈 + 入库。 */
  const handleStrokeCommit = useCallback((page, stroke) => {
    pushHistory([page]);
    strokesRef.current[page] = [...(strokesRef.current[page] || []), stroke];
  }, [pushHistory]);

  const applyEntry = useCallback((entry) => {
    // 返回「应用之前的样子」，方便反向压进另一个栈
    const inverse = entry.map(({ page }) => ({ page, prev: strokesRef.current[page] || [] }));
    entry.forEach(({ page, prev }) => { strokesRef.current[page] = prev; });
    setRedrawToken(t => t + 1);
    changeSeqRef.current += 1;
    setNotesDirty(true);
    return inverse;
  }, []);

  const handleUndo = useCallback(() => {
    const entry = historyRef.current.past.pop();
    if (!entry) return;
    historyRef.current.future.push(applyEntry(entry));
    setCanUndo(historyRef.current.past.length > 0);
    setCanRedo(true);
  }, [applyEntry]);

  /** 把当前笔迹整理成后端要的形状（空页不上传，省流量也省得存一堆空数组）。 */
  const collectPages = useCallback(() => {
    const pages = {};
    Object.keys(strokesRef.current).forEach(page => {
      const strokes = strokesRef.current[page];
      if (strokes && strokes.length) pages[page] = strokes;
    });
    return pages;
  }, []);

  /**
   * 保存到云端。**只在老师主动点 Save notes 时调用** —— 按校方要求笔记默认不留存，
   * 关闭教材即清除，主动保存的才留 7 天。
   *
   * 笔记为空时改为删除记录 —— 否则老师清空后重新打开又会看到旧笔记。
   * 没有 materialId（例如调试页）就什么都不做，标注只在本次会话内有效。
   */
  const saveNotes = useCallback(async () => {
    if (!materialId) return false;
    const pages = collectPages();
    const seqAtSend = changeSeqRef.current;
    setNotesStatus('saving');
    try {
      if (Object.keys(pages).length === 0) {
        await deleteMaterialNotes(materialId);
        if (changeSeqRef.current === seqAtSend) setNotesDirty(false);
        setNotesSavedAt(null);
        setNotesStatus('idle');
        showHint('Your saved notes have been removed.');
        return true;
      }
      await saveMaterialNotes(materialId, pages);
      // 请求期间又有新改动的话保持 dirty，提醒老师再存一次（不会自动补存）
      if (changeSeqRef.current === seqAtSend) setNotesDirty(false);
      setNotesSavedAt(new Date());
      setNotesStatus('saved');
      showHint(`Notes saved — kept for ${RETENTION_DAYS} days, then cleared automatically.`);
      return true;
    } catch (err) {
      console.error('Failed to save material notes:', err);
      setNotesStatus('error');
      showHint(`Could not save your notes: ${err.message}`);
      return false;
    }
  }, [materialId, collectPages, showHint]);

  // 打开教材时把云端笔记载回来（每次打开只载一次）
  useEffect(() => {
    if (!show || !materialId) return;
    const loadKey = `${materialId}`;
    if (loadedForRef.current === loadKey) return;
    loadedForRef.current = loadKey;

    let cancelled = false;
    setNotesStatus('loading');
    getMaterialNotes(materialId)
      .then(result => {
        if (cancelled) return;
        if (result.found && result.notes && result.notes.pages) {
          strokesRef.current = { ...result.notes.pages };
          historyRef.current = { past: [], future: [] };
          setCanUndo(false);
          setCanRedo(false);
          setNotesDirty(false);
          setNotesSavedAt(result.updatedAt ? new Date(result.updatedAt) : null);
          setRedrawToken(t => t + 1);
          showHint(`Loaded the notes you saved (${result.strokeCount} marks on ${result.pageCount} page${result.pageCount > 1 ? 's' : ''}). Save again to keep them another ${RETENTION_DAYS} days.`);
        }
        setNotesStatus('idle');
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load material notes:', err);
        setNotesStatus('error');
        showHint(`Could not load your saved notes: ${err.message}`);
      });

    return () => { cancelled = true; };
  }, [show, materialId, showHint]);

  // 关闭弹窗时清掉「已载入」标记，下次打开重新拉一次
  useEffect(() => {
    if (!show) loadedForRef.current = null;
  }, [show]);

  // 这里原本有一个 2.5s 防抖的自动保存。2026-08-25 按校方要求去掉：
  // 笔记默认只在本次打开有效，只有老师主动点 Save notes 才上云（并保留 7 天）。

  const handleRedo = useCallback(() => {
    const entry = historyRef.current.future.pop();
    if (!entry) return;
    historyRef.current.past.push(applyEntry(entry));
    setCanRedo(historyRef.current.future.length > 0);
    setCanUndo(true);
  }, [applyEntry]);

  // 翻页模式那块画布：尺寸变化 / 需要重绘时回放当前页的笔迹。
  // 依赖里带上 continuousScroll —— 从连续滚动切回翻页模式时画布是「新挂载」的元素，
  // 而 pageDimensions 可能早已就绪不再变化，不重跑这个 effect 的话新画布会停留在默认 300x150。
  useEffect(() => {
    if (continuousScroll) return;
    const canvas = canvasRef.current;
    if (!canvas || pageDimensions.width <= 0 || pageDimensions.height <= 0) return;
    canvas.width = pageDimensions.width;
    canvas.height = pageDimensions.height;
    redrawCanvas(canvas, strokesRef.current[pageNumber]);
  }, [pageDimensions, continuousScroll, pageNumber, redrawToken]);

  // Handle print for Print mode
  const handlePrint = useCallback(() => {
    if (mode !== 'print') return;

    // Open PDF in new window for printing
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }

    if (onPrintComplete) {
      onPrintComplete();
    }
  }, [mode, pdfUrl, onPrintComplete]);

  // Handle page load success —— 记录页面原始尺寸（与缩放无关），渲染宽度由容器自适应
  const onPageLoadSuccess = (page) => {
    const naturalW = page.originalWidth || page.width;
    const naturalH = page.originalHeight || page.height;
    if (naturalW && naturalH) {
      setNaturalPageSize({ width: naturalW, height: naturalH });
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF. Please try again.');
    setLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setZoomMultiplier(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setZoomMultiplier(prev => Math.max(prev - 0.2, 0.4));
  };

  const resetZoom = () => {
    setZoomMultiplier(1.0);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (modalRef.current?.requestFullscreen) {
        modalRef.current.requestFullscreen();
      } else if (modalRef.current?.webkitRequestFullscreen) {
        modalRef.current.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  const fingerHintShownRef = useRef(false);
  useEffect(() => {
    if (!isAnnotating) fingerHintShownRef.current = false;
  }, [isAnnotating]);

  const handleFingerScroll = useCallback(() => {
    if (fingerHintShownRef.current) return;
    fingerHintShownRef.current = true;
    showHint('Finger scrolls the material. Tap "Finger: Draw" to write with your finger.');
  }, [showHint]);

  // 首次检测到手写笔：自动变成「笔书写 / 手指滚动」，并告诉老师发生了什么
  const handlePenSeen = useCallback(() => {
    if (penStateRef.current.announced) return;
    penStateRef.current.announced = true;
    setPenDetected(true);
    if (fingerDrawPref === null) {
      showHint('Apple Pencil detected. Pencil writes, finger scrolls.');
    }
  }, [showHint, fingerDrawPref]);

  // Ctrl/Cmd+Z 撤销、Ctrl/Cmd+Shift+Z（或 Ctrl+Y）重做。iPad 外接键盘也吃这套。
  useEffect(() => {
    if (!show || !isAnnotating) return undefined;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [show, isAnnotating, handleUndo, handleRedo]);

  // Toggle annotation mode
  // 翻页模式用单页画布；连续滚动模式每页各有一块画布（ScrollAnnotatablePage）。
  // 两种模式都支持标注，因此进入标注时不再强制切回翻页模式，老师停留在当前滚动位置即可书写。
  const enterAnnotation = () => {
    setIsAnnotating(true);
    setIsEraser(false);
    setIsTextMode(false);
  };

  const toggleAnnotation = () => {
    if (isAnnotating) {
      setTextPosition(null);
      setTextInput('');
      setIsAnnotating(false);
      setIsEraser(false);
      setIsTextMode(false);
      return;
    }
    // 开始标注之前先告知「笔记不会自动保存」（勾过不再提醒、或调试页没有 materialId 时跳过）
    if (materialId && !isNoticeDismissed()) {
      setNoticeDontShowAgain(false);
      setShowNotSavedNotice(true);
      return;
    }
    enterAnnotation();
  };

  // 弹窗上的「Start annotating」：记住是否不再提醒，然后真正进入标注模式
  const acceptNotSavedNotice = () => {
    if (noticeDontShowAgain) rememberNoticeDismissed();
    setShowNotSavedNotice(false);
    enterAnnotation();
  };

  // 弹窗上的 X / Cancel：不进入标注模式，也不记「不再提醒」
  const dismissNotSavedNotice = () => {
    setShowNotSavedNotice(false);
  };

  // 切换 连续滚动 / 翻页 模式
  const toggleScrollMode = () => {
    if (isAnnotating) return; // 标注时禁用
    setContinuousScroll(prev => !prev);
  };

  // ===== 左右滑动翻页（仅翻页模式、非标注时生效）=====
  const handleSwipeStart = (e) => {
    if (continuousScroll || isAnnotating) return;
    if (!e.touches || e.touches.length !== 1) { swipeRef.current = null; return; }
    swipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handleSwipeEnd = (e) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start || continuousScroll || isAnnotating) return;
    const touch = (e.changedTouches && e.changedTouches[0]);
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    // 仅当水平移动明显且占主导时翻页，避免和竖向滚动/缩放冲突
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) {
        goToNextPage();
      } else {
        goToPrevPage();
      }
    }
  };

  // 清空当前页：作为一次可撤销的操作
  const clearAnnotations = () => {
    setTextPosition(null);
    setTextInput('');
    if (!(strokesRef.current[pageNumber] || []).length) return;
    pushHistory([pageNumber]);
    strokesRef.current[pageNumber] = [];
    setRedrawToken(t => t + 1);
  };

  // 清空全部页面：同样进历史栈，一次 Undo 就能全部找回
  const clearAllAnnotations = () => {
    const pages = Object.keys(strokesRef.current).filter(k => (strokesRef.current[k] || []).length);
    if (!pages.length) return;
    if (!window.confirm('Clear annotations on ALL pages?')) return;
    setTextPosition(null);
    setTextInput('');
    pushHistory(pages);
    pages.forEach(page => { strokesRef.current[page] = []; });
    setRedrawToken(t => t + 1);
  };

  // 翻页模式那块画布的重绘（回滚被双指打断的半截笔画时用）
  const redrawPageCanvas = useCallback(() => {
    redrawCanvas(canvasRef.current, strokesRef.current[pageNumber]);
  }, [pageNumber]);

  const commitPageStroke = useCallback((stroke) => {
    handleStrokeCommit(pageNumber, stroke);
  }, [handleStrokeCommit, pageNumber]);

  // 文字工具：把输入框里的文字落成一条 text 笔画（可撤销、可保存、可回放）
  const commitTextStroke = useCallback((pos, value) => {
    const canvas = canvasRef.current;
    if (!canvas || !value.trim()) return;
    handleStrokeCommit(pageNumber, {
      t: 'text',
      c: brushColor,
      s: fontSize / canvas.width,
      x: pos.x / canvas.width,
      y: pos.y / canvas.height,
      v: value,
    });
    setRedrawToken(t => t + 1);
  }, [brushColor, fontSize, handleStrokeCommit, pageNumber]);

  const renderTextToCanvas = useCallback(() => {
    if (textPosition && textInput.trim()) commitTextStroke(textPosition, textInput);
    setTextPosition(null);
    setTextInput('');
  }, [commitTextStroke, textInput, textPosition]);

  const cancelTextInput = useCallback(() => {
    setTextPosition(null);
    setTextInput('');
  }, []);

  // 文字工具：点击画布放置输入框（已有未确认的文字先落到画布）
  // 文字工具：点击画布放置输入框（已有未确认的文字先落成一条 text 笔画）
  const handleTextTap = (pos) => {
    if (textPosition && textInput.trim()) commitTextStroke(textPosition, textInput);
    setTextPosition(pos);
    setTextInput('');
  };

  const pagePointerHandlers = useAnnotationPointer({
    canvasRef,
    scrollContainerRef: modalContentRef,
    isAnnotating,
    isEraser,
    isTextMode,
    brushColor,
    brushSize,
    fingerDrawing,
    penStateRef,
    onPenSeen: handlePenSeen,
    onFingerScroll: handleFingerScroll,
    onTextTap: handleTextTap,
    onStrokeCommit: commitPageStroke,
    redrawPage: redrawPageCanvas,
    onDebug: onPointerDebug,
  });

  const handleTextKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelTextInput();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      renderTextToCanvas();
    }
    e.stopPropagation();
  };

  // Handle close
  // 按校方要求：关闭教材 = 这次课用完了，未主动保存的笔记直接丢弃，
  // **关闭时不弹任何窗**（老师在进入标注前已经被告知过一次了）。
  const handleClose = () => {
    strokesRef.current = {};
    historyRef.current = { past: [], future: [] };
    setCanUndo(false);
    setCanRedo(false);
    setNotesDirty(false);
    setIsAnnotating(false);
    setShowNotSavedNotice(false);
    setIsFullscreen(false);
    setIsTextMode(false);
    setTextPosition(null);
    setTextInput('');
    setPageNumber(1);

    // Exit fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }

    onClose();
  };

  // 工具栏上那行小字：让老师随时知道笔记有没有存上。
  // 默认不保存，所以「有笔迹但没存」要说得直白 —— 说成 "Unsaved changes" 会让人以为待会儿会自动存。
  const notesStatusText = (() => {
    if (!materialId) return '';
    if (notesStatus === 'loading') return 'Loading notes…';
    if (notesStatus === 'saving') return 'Saving…';
    if (notesStatus === 'error') return 'Not saved';
    if (notesDirty) return 'Not saved — will be lost when you close';
    if (notesSavedAt) {
      return `Saved ${notesSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · kept ${RETENTION_DAYS} days`;
    }
    return '';
  })();

  if (!show) return null;

  return (
    <ModalOverlay>
      <Modal ref={modalRef} $fullscreen={isFullscreen}>
        <ModalHeader>
          <h3>
            <TitleText>{materialName}</TitleText>
            <ModeBadge mode={mode} style={{ flexShrink: 0 }}>{mode === 'print' ? 'Print Mode' : 'View Only'}</ModeBadge>
          </h3>
          <HeaderButtons>
            {!isAnnotating && (
              <IconButton
                onClick={toggleScrollMode}
                $active={continuousScroll}
                title="Switch scroll / paged view"
              >
                {continuousScroll ? 'Scroll' : 'Pages'}
              </IconButton>
            )}
            <IconButton
              onClick={toggleAnnotation}
              $active={isAnnotating}
              title={isAnnotating
                ? 'Exit Annotation Mode'
                : 'Annotate — notes are not saved automatically. Use "Save notes" if you want to keep them.'}
            >
              {isAnnotating ? 'Exit Annotate' : 'Annotate'}
            </IconButton>
            <IconButton
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </IconButton>
            <CloseButton onClick={handleClose}>&times;</CloseButton>
          </HeaderButtons>
        </ModalHeader>

        {/* 「笔记不会自动保存」告知弹窗 —— 按规范不支持点背景关闭，只能 X / Cancel / Start */}
        {showNotSavedNotice && (
          <NoticeOverlay>
            <NoticeCard role="dialog" aria-modal="true" aria-labelledby="annotation-notice-title">
              <NoticeHeader>
                <h4 id="annotation-notice-title">✏️ Notes are not saved automatically</h4>
                <CloseButton onClick={dismissNotSavedNotice} title="Close" aria-label="Close">×</CloseButton>
              </NoticeHeader>
              <NoticeBody>
                <p>
                  Anything you write on this material is <strong>only kept while it stays open</strong>.
                  Closing the material clears it.
                </p>
                <p>
                  Want to keep your notes for the next lesson? Click <strong>Save notes</strong> in the
                  annotation toolbar — saved notes stay on your account for <strong>{RETENTION_DAYS} days</strong>,
                  then are cleared automatically.
                </p>
              </NoticeBody>
              <NoticeFooter>
                <NoticeCheckboxLabel>
                  <input
                    type="checkbox"
                    checked={noticeDontShowAgain}
                    onChange={(e) => setNoticeDontShowAgain(e.target.checked)}
                  />
                  Don&apos;t show this again
                </NoticeCheckboxLabel>
                <NoticeButtonRow>
                  <NoticeSecondaryButton onClick={dismissNotSavedNotice}>Cancel</NoticeSecondaryButton>
                  <NoticePrimaryButton onClick={acceptNotSavedNotice}>Start annotating</NoticePrimaryButton>
                </NoticeButtonRow>
              </NoticeFooter>
            </NoticeCard>
          </NoticeOverlay>
        )}

        {/* Annotation Toolbar */}
        {isAnnotating && (
          <AnnotationToolbar>
            <ToolGroup>
              <ToolLabel>Color:</ToolLabel>
              {COLORS.map(color => (
                <ColorButton
                  key={color}
                  $color={color}
                  $selected={brushColor === color && !isEraser}
                  onClick={() => { setBrushColor(color); if (!isTextMode) setIsEraser(false); }}
                />
              ))}
            </ToolGroup>

            <ToolGroup>
              {isTextMode ? (
                <>
                  <ToolLabel>Font:</ToolLabel>
                  <SizeSlider
                    type="range"
                    min="12"
                    max="48"
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                  />
                  <span style={{ fontSize: '12px', color: '#92400e', minWidth: '30px' }}>{fontSize}px</span>
                </>
              ) : (
                <>
                  <ToolLabel>Size:</ToolLabel>
                  <SizeSlider
                    type="range"
                    min="1"
                    max="20"
                    value={brushSize}
                    onChange={(e) => setBrushSize(parseInt(e.target.value))}
                  />
                  <span style={{ fontSize: '12px', color: '#92400e', minWidth: '24px' }}>{brushSize}px</span>
                </>
              )}
            </ToolGroup>

            <ToolGroup>
              <ToolButton onClick={handleUndo} disabled={!canUndo} title="Undo (Ctrl/Cmd+Z)">
                ↶ Undo
              </ToolButton>
              <ToolButton onClick={handleRedo} disabled={!canRedo} title="Redo (Ctrl/Cmd+Shift+Z)">
                ↷ Redo
              </ToolButton>
            </ToolGroup>

            <ToolGroup>
              <ToolButton $active={!isEraser && !isTextMode} onClick={() => { setIsEraser(false); setIsTextMode(false); cancelTextInput(); }}>
                Pen
              </ToolButton>
              <ToolButton $active={isEraser} onClick={() => { setIsEraser(true); setIsTextMode(false); cancelTextInput(); }}>
                Eraser
              </ToolButton>
              <ToolButton $active={isTextMode} onClick={() => { setIsTextMode(true); setIsEraser(false); }}>
                Text
              </ToolButton>
            </ToolGroup>

            {/* 触摸设备才有意义：手指负责书写还是滚动。默认自动（有笔→笔书写，无笔→手指书写） */}
            {isTouchDevice && (
              <ToolGroup>
                <ToolButton
                  $active={fingerDrawing}
                  onClick={() => setFingerDrawPref(!fingerDrawing)}
                  title={fingerDrawing
                    ? 'Finger writes. Drag with two fingers to scroll. Tap to switch back.'
                    : 'Finger scrolls, stylus/pen writes. Tap to write with your finger.'}
                >
                  {fingerDrawing ? '✍️ Finger: Draw' : '🖐 Finger: Scroll'}
                </ToolButton>
              </ToolGroup>
            )}

            <ToolGroup>
              {!continuousScroll && (
                <ClearButton onClick={clearAnnotations}>
                  Clear Page
                </ClearButton>
              )}
              <ClearButton onClick={clearAllAnnotations}>
                Clear All
              </ClearButton>
            </ToolGroup>

            {/* 云端笔记：没有 materialId（例如调试页）时不显示，避免误导 */}
            {materialId && (
              <ToolGroup>
                <ToolButton
                  onClick={saveNotes}
                  disabled={notesStatus === 'saving'}
                  title={`Notes are not saved automatically. Click to keep them on your account for ${RETENTION_DAYS} days.`}
                >
                  {notesStatus === 'saving' ? 'Saving…' : 'Save notes'}
                </ToolButton>
                <ToolLabel>{notesStatusText}</ToolLabel>
              </ToolGroup>
            )}
          </AnnotationToolbar>
        )}

        <ModalContent
          ref={modalContentRef}
          $viewOnly={mode === 'view'}
          $annotating={isAnnotating}
          onTouchStart={handleSwipeStart}
          onTouchEnd={handleSwipeEnd}
        >
          {loading && (
            <LoadingContainer>
              <div>Loading PDF...</div>
            </LoadingContainer>
          )}

          {error && (
            <ErrorContainer>
              {error}
            </ErrorContainer>
          )}

          {pdfUrl && (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
            >
              {continuousScroll ? (
                /* 连续滚动模式：所有页竖向堆叠，上下滑动查看（适合手机/iPad）；
                   每页自带标注画布，老师可直接在当前浏览到的页面上书写，无需切回翻页模式 */
                <PagesScroll>
                  {Array.from({ length: numPages || 0 }, (_, i) => (
                    <ScrollAnnotatablePage
                      key={`page_${i + 1}`}
                      pageNumber={i + 1}
                      numPages={numPages}
                      renderWidth={renderWidth}
                      isAnnotating={isAnnotating}
                      isEraser={isEraser}
                      isTextMode={isTextMode}
                      brushColor={brushColor}
                      brushSize={brushSize}
                      fontSize={fontSize}
                      fingerDrawing={fingerDrawing}
                      strokesRef={strokesRef}
                      redrawToken={redrawToken}
                      onStrokeCommit={handleStrokeCommit}
                      onNaturalSize={i === 0 ? setNaturalPageSize : undefined}
                      scrollContainerRef={modalContentRef}
                      penStateRef={penStateRef}
                      onPenSeen={handlePenSeen}
                      onFingerScroll={handleFingerScroll}
                      onDebug={onPointerDebug}
                    />
                  ))}
                </PagesScroll>
              ) : (
                /* 翻页模式：单页 + 标注画布；可左右滑动翻页 */
                <PdfContainer ref={pdfContainerRef}>
                  <Page
                    pageNumber={pageNumber}
                    width={renderWidth}
                    renderTextLayer={!isAnnotating}
                    renderAnnotationLayer={!isAnnotating}
                    onLoadSuccess={onPageLoadSuccess}
                  />

                  {/* Annotation Canvas Overlay */}
                  <AnnotationCanvas
                    ref={canvasRef}
                    $active={isAnnotating}
                    $eraser={isEraser}
                    $textMode={isTextMode}
                    {...pagePointerHandlers}
                  />

                  {/* Text input overlay */}
                  {textPosition && (
                    <div style={{
                      position: 'absolute',
                      left: `${textPosition.x}px`,
                      top: `${textPosition.y}px`,
                      zIndex: 10,
                    }}>
                      <TextInputOverlay
                        ref={textareaRef}
                        value={textInput}
                        onChange={(e) => {
                          setTextInput(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onKeyDown={handleTextKeyDown}
                        $color={brushColor}
                        $fontSize={fontSize}
                        autoFocus
                        rows={1}
                      />
                      <TextConfirmBar>
                        <TextConfirmButton $primary onClick={renderTextToCanvas}>OK</TextConfirmButton>
                        <TextConfirmButton onClick={cancelTextInput}>Cancel</TextConfirmButton>
                      </TextConfirmBar>
                    </div>
                  )}
                </PdfContainer>
              )}
            </Document>
          )}

          {hint && <HintToast>{hint}</HintToast>}
        </ModalContent>

        <ModalFooter>
          <PageControls>
            {continuousScroll ? (
              <PageInfo>
                {numPages ? `${numPages} page${numPages > 1 ? 's' : ''} · scroll to view` : 'Loading…'}
              </PageInfo>
            ) : (
              <>
                <PageButton onClick={goToPrevPage} disabled={pageNumber <= 1}>
                  Previous
                </PageButton>
                <PageInfo>
                  Page {pageNumber} of {numPages || '?'}
                </PageInfo>
                <PageButton onClick={goToNextPage} disabled={pageNumber >= (numPages || 1)}>
                  Next
                </PageButton>
              </>
            )}

            <ZoomControls>
              <ZoomButton onClick={zoomOut} disabled={zoomMultiplier <= 0.4} title="Zoom Out">
                -
              </ZoomButton>
              <ZoomInfo>{Math.round(zoomMultiplier * 100)}%</ZoomInfo>
              <ZoomButton onClick={zoomIn} disabled={zoomMultiplier >= 3.0} title="Zoom In">
                +
              </ZoomButton>
              <ZoomButton onClick={resetZoom} title="Fit to width" style={{ fontSize: '12px' }}>
                Fit
              </ZoomButton>
            </ZoomControls>
          </PageControls>

          <ActionButtons>
            {mode === 'print' && (
              <Button $primary onClick={handlePrint}>
                Print
              </Button>
            )}
            <Button onClick={handleClose}>
              Close
            </Button>
          </ActionButtons>
        </ModalFooter>
      </Modal>
    </ModalOverlay>
  );
};

export default MaterialPdfViewerModal;
